import React, { useContext, useMemo, useState } from 'react';
import { useLegalEntitiesStore } from '../store';
import { useMutation } from 'react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LegalEntitiesDrawer } from './LegalEntitiesDrawer';
import { EditLegalEntitiesDrawerContext } from './EditLegalEntitiesDrawerContext';
import PropTypes from 'prop-types';
import { z } from 'zod';
import { required } from '../../../../../../utils/validation/zodValidators';
import { Modes } from './constants';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

const FormModel = z.object({
    name: required().default(''),
    description: z
        .string()
        .trim()
        .nullable(),
    street: required(),
    city: required(),
    country: required(),
    zipCode: required(),
    vatCountry: required(),
    vatNumber: required()
});

const getModeById = id => (id ? Modes.EDIT : Modes.CREATE);

const fieldsToBackend = fields => ({
    name: fields.name,
    name_on_head: fields.name,
    description: fields.description,
    address: {
        street_line1: fields.street,
        city: fields.city,
        postal_code: fields.zipCode,
        country: fields.country
    },
    vat_country: fields.vatCountry,
    vat_number: fields.vatNumber
});

const fieldsFromBackend = data => ({
    name: data.name,
    description: data.description,
    street: data.address?.street_line1,
    city: data.address?.city,
    zipCode: data.address?.postal_code,
    country: data.address?.country,
    vatCountry: data.vat_country,
    vatNumber: data.vat_number
});

const EMPTY_STATE = {
    name: '',
    description: '',
    street: '',
    city: '',
    country: '',
    zipCode: '',
    vatCountry: '',
    vatNumber: ''
};

export const EditLegalEntitiesDrawerProvider = ({ children }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const mode = getModeById(editId);
    const store = useLegalEntitiesStore();
    const toaster = useContext(ToastContext);

    const closeModal = () => setIsOpen(false);

    const createMutation = useMutation(data => store.createEntity(data), {
        onSuccess: () => {
            closeModal();
            toaster.success(t('sauron.financeModule.documentHeads.drawer.create.successToast'));
        }
    });

    const updateMutation = useMutation(data => store.updateEntity(editId, data), {
        onSuccess: () => {
            closeModal();
            toaster.success(t('sauron.financeModule.documentHeads.drawer.edit.successToast'));
        }
    });

    const form = useForm({
        resolver: zodResolver(FormModel)
    });

    const { reset } = form;

    const handleSubmit = formData => {
        switch (mode) {
            case Modes.CREATE:
                createMutation.mutate(fieldsToBackend(formData));
                break;
            case Modes.EDIT:
                updateMutation.mutate(fieldsToBackend(formData));
                break;
            default:
                break;
        }
    };

    const value = useMemo(() => {
        const edit = document => {
            setIsOpen(true);
            setEditId(document.id);
            reset(fieldsFromBackend(document));
        };

        const create = () => {
            setIsOpen(true);
            setEditId(null);
            reset(EMPTY_STATE);
        };

        return {
            edit,
            create
        };
    }, [reset]);

    return (
        <EditLegalEntitiesDrawerContext.Provider value={value}>
            <FormProvider {...form}>
                <LegalEntitiesDrawer
                    isOpen={isOpen}
                    mode={mode}
                    onClose={closeModal}
                    onSubmit={form.handleSubmit(handleSubmit)}
                    isSubmitting={updateMutation.isLoading || createMutation.isLoading}
                />
            </FormProvider>
            {children}
        </EditLegalEntitiesDrawerContext.Provider>
    );
};

EditLegalEntitiesDrawerProvider.propTypes = {
    children: PropTypes.node
};
